import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  EmptyState,
  ErrorState,
  Loading,
  Badge,
  AppBar,
  BottomNav,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardTitle,
  Icon,
  Image,
  Input,
  List,
  ListItem,
  type BottomNavItem,
} from '@ui-kit';
import {
  CalendarDays,
  BriefcaseBusiness,
  GraduationCap,
  Gavel,
  HeartPulse,
  PawPrint,
  PlayCircle,
  Rocket,
  Search,
  Settings2,
  Shield,
  ShoppingBasket,
  Stethoscope,
  Store,
  Utensils,
  Home,
  Bot,
  Megaphone,
  MessagesSquare,
  Send,
  type LucideIcon,
} from 'lucide-react';
import { CardGrid, ScreenScaffold, SectionHeader } from '@miniapp-core/components';
import { useAsyncData } from '@frontend-core/hooks';
import { request } from '../utils/api-client';
import { getTenantApiPath } from './routing';
import type { StaffRolesMap } from './context/StaffRolesContext';
import { useTenantTelegram, type TenantTelegramLink } from '@frontend-core/telegram';

interface ServiceDashboardProps {
  activeServices: string[];
  staffRoles: StaffRolesMap;
  servicesLoading: boolean;
  servicesError: string | null;
}

interface ServiceDescriptor {
  route: string;
  titleKey: string;
  descriptionKey: string;
}

interface CategoryDescriptor {
  id: string;
  icon: typeof ShoppingBasket;
  titleKey: string;
  variant: 'primary' | 'neutral' | 'error' | 'success';
  serviceId?: string;
}

interface StaffToolDescriptor {
  canAccess: (roles: Record<string, boolean>) => boolean;
  icon: typeof Settings2;
  route: string;
  titleKey: string;
  descriptionKey: string;
}

const SERVICE_DESCRIPTORS: Record<string, ServiceDescriptor> = {
  ecommerce: {
    route: '/ecommerce/',
    titleKey: 'services.ecommerce.title',
    descriptionKey: 'services.ecommerce.description',
  },
  marketplace: {
    route: '/marketplace/',
    titleKey: 'services.marketplace.title',
    descriptionKey: 'services.marketplace.description',
  },
  events: {
    route: '/events/',
    titleKey: 'services.events.title',
    descriptionKey: 'services.events.description',
  },
  'digital-media': {
    route: '/digital-media/',
    titleKey: 'services.digitalMedia.title',
    descriptionKey: 'services.digitalMedia.description',
  },
  'health-and-beauty': {
    route: '/health-and-beauty/',
    titleKey: 'services.healthBeauty.title',
    descriptionKey: 'services.healthBeauty.description',
  },
  'pet-zone': {
    route: '/pet-zone/',
    titleKey: 'categories.petZone',
    descriptionKey: 'dashboard.subtitle',
  },
  education: {
    route: '/education/',
    titleKey: 'services.education.title',
    descriptionKey: 'services.education.description',
  },
  restaurants: {
    route: '/restaurants/',
    titleKey: 'services.restaurants.title',
    descriptionKey: 'services.restaurants.description',
  },
  'freelance-services': {
    route: '/freelance-services/',
    titleKey: 'categories.services',
    descriptionKey: 'dashboard.subtitle',
  },
  'real-estate': {
    route: '/real-estate/',
    titleKey: 'categories.rent',
    descriptionKey: 'dashboard.subtitle',
  },
};

const CATEGORIES: CategoryDescriptor[] = [
  {
    id: 'marketplace',
    icon: ShoppingBasket,
    titleKey: 'categories.marketplace',
    variant: 'primary',
    serviceId: 'marketplace',
  },
  {
    id: 'shops',
    icon: Store,
    titleKey: 'categories.shops',
    variant: 'primary',
    serviceId: 'ecommerce',
  },
  {
    id: 'events',
    icon: CalendarDays,
    titleKey: 'categories.eventsBot',
    variant: 'error',
    serviceId: 'events',
  },
  {
    id: 'digital-media',
    icon: PlayCircle,
    titleKey: 'categories.digitalMedia',
    variant: 'neutral',
    serviceId: 'digital-media',
  },
  {
    id: 'health-and-beauty',
    icon: HeartPulse,
    titleKey: 'categories.healthBeauty',
    variant: 'success',
    serviceId: 'health-and-beauty',
  },
  {
    id: 'pet-zone',
    icon: PawPrint,
    titleKey: 'categories.petZone',
    variant: 'success',
    serviceId: 'pet-zone',
  },
  {
    id: 'education',
    icon: GraduationCap,
    titleKey: 'categories.education',
    variant: 'primary',
    serviceId: 'education',
  },
  {
    id: 'restaurants',
    icon: Utensils,
    titleKey: 'categories.restaurants',
    variant: 'primary',
    serviceId: 'restaurants',
  },
  {
    id: 'freelance-services',
    icon: BriefcaseBusiness,
    titleKey: 'categories.services',
    variant: 'neutral',
    serviceId: 'freelance-services',
  },
  {
    id: 'real-estate',
    icon: Home,
    titleKey: 'categories.rent',
    variant: 'primary',
    serviceId: 'real-estate',
  },
];

const STAFF_TOOL_DESCRIPTORS: Record<string, StaffToolDescriptor> = {
  ecommerce: {
    canAccess: (roles) => Boolean(roles.isManager || roles.isCourier),
    icon: Settings2,
    route: '/ecommerce/manager/',
    titleKey: 'staff.ecommerce.manager.title',
    descriptionKey: 'staff.ecommerce.manager.description',
  },
  marketplace: {
    canAccess: (roles) => Boolean(roles.isModerator),
    icon: Shield,
    route: '/marketplace/moderator/',
    titleKey: 'staff.marketplace.moderator.title',
    descriptionKey: 'staff.marketplace.moderator.description',
  },
  events: {
    canAccess: (roles) => Boolean(roles.isManager),
    icon: CalendarDays,
    route: '/events/manager/',
    titleKey: 'staff.events.manager.title',
    descriptionKey: 'staff.events.manager.description',
  },
  'digital-media': {
    canAccess: (roles) => Boolean(roles.isManager),
    icon: PlayCircle,
    route: '/digital-media/manager/',
    titleKey: 'staff.digitalMedia.manager.title',
    descriptionKey: 'staff.digitalMedia.manager.description',
  },
  'health-and-beauty': {
    canAccess: (roles) => Boolean(roles.isManager),
    icon: Stethoscope,
    route: '/health-and-beauty/manager/',
    titleKey: 'staff.healthBeauty.manager.title',
    descriptionKey: 'staff.healthBeauty.manager.description',
  },
  'pet-zone': {
    canAccess: (roles) => Boolean(roles.isManager),
    icon: PawPrint,
    route: '/pet-zone/manager/',
    titleKey: 'categories.petZone',
    descriptionKey: 'dashboard.subtitle',
  },
  education: {
    canAccess: (roles) => Boolean(roles.isManager),
    icon: GraduationCap,
    route: '/education/manager/',
    titleKey: 'staff.education.manager.title',
    descriptionKey: 'staff.education.manager.description',
  },
  restaurants: {
    canAccess: (roles) => Boolean(roles.isManager),
    icon: Utensils,
    route: '/restaurants/manager/',
    titleKey: 'staff.restaurants.manager.title',
    descriptionKey: 'staff.restaurants.manager.description',
  },
  'freelance-services': {
    canAccess: (roles) => Boolean(roles.isArbitrator || roles.isSupport),
    icon: Gavel,
    route: '/freelance-services/manager/',
    titleKey: 'categories.services',
    descriptionKey: 'dashboard.subtitle',
  },
  'real-estate': {
    canAccess: (roles) => Boolean(roles.isManager),
    icon: Home,
    route: '/real-estate/manager/',
    titleKey: 'categories.rent',
    descriptionKey: 'dashboard.subtitle',
  },
};

const TELEGRAM_LINK_ICONS: Record<TenantTelegramLink['kind'], LucideIcon> = {
  CHANNEL: Megaphone,
  GROUP: MessagesSquare,
  BOT: Bot,
  OTHER: Send,
};

function isPositiveNumericId(value: string | null): value is string {
  if (typeof value !== 'string' || !/^\d+$/.test(value)) {
    return false;
  }
  return Number.parseInt(value, 10) > 0;
}

export default function ServiceDashboard({
  activeServices,
  staffRoles,
  servicesLoading,
  servicesError,
}: ServiceDashboardProps) {
  const { t } = useTranslation('shell');
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [query]);
  const discovery = useAsyncData(
    async (signal) => {
      const response = await request<{
        data: Array<{
          id: number;
          service: string;
          title: string;
          imageUrl: string | null;
          route: string;
        }>;
      }>(getTenantApiPath(`/discovery?q=${encodeURIComponent(debouncedQuery)}`), { signal });
      return response.data;
    },
    [debouncedQuery, activeServices.join(',')],
    { keepPreviousData: false },
  );
  const visibleCategories = CATEGORIES.filter(
    (category) =>
      !query.trim() ||
      (category.serviceId &&
        activeServices.includes(category.serviceId) &&
        `${t(category.titleKey)} ${t(SERVICE_DESCRIPTORS[category.serviceId]?.descriptionKey ?? '')}`
          .toLocaleLowerCase()
          .includes(query.trim().toLocaleLowerCase())),
  );
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { info: telegramInfo } = useTenantTelegram();

  const bottomNavItems: readonly BottomNavItem[] = telegramInfo.links
    .filter((link) => link.showInMiniapp)
    .map((link) => ({
      value: String(link.id),
      href: link.url,
      label: link.label,
      icon: <Icon icon={TELEGRAM_LINK_ICONS[link.kind]} size={18} aria-hidden="true" />,
    }));

  const requestedService = searchParams.get('service')?.trim().toLowerCase() ?? '';
  const legacyOrderId = searchParams.get('orderId');
  const legacyDeliveryOrderId = searchParams.get('deliveryOrderId');

  const availableServices = useMemo(
    () => activeServices.filter((service) => SERVICE_DESCRIPTORS[service]),
    [activeServices],
  );

  useEffect(() => {
    if (!requestedService || servicesLoading || servicesError) {
      return;
    }
    const descriptor = SERVICE_DESCRIPTORS[requestedService];
    if (!descriptor) return;
    if (!availableServices.includes(requestedService)) return;
    navigate(descriptor.route, { replace: true });
  }, [availableServices, navigate, requestedService, servicesError, servicesLoading]);

  useEffect(() => {
    if (servicesLoading || servicesError) return;
    if (!availableServices.includes('ecommerce')) return;
    if (isPositiveNumericId(legacyOrderId)) {
      navigate(`/ecommerce/manager/?orderId=${legacyOrderId}`, { replace: true });
      return;
    }
    if (isPositiveNumericId(legacyDeliveryOrderId)) {
      navigate(`/ecommerce/?deliveryOrderId=${legacyDeliveryOrderId}`, { replace: true });
    }
  }, [
    availableServices,
    legacyDeliveryOrderId,
    legacyOrderId,
    navigate,
    servicesError,
    servicesLoading,
  ]);

  return (
    <ScreenScaffold
      maxWidth="2xl"
      header={
        <AppBar position="static" title={t('dashboard.title')} subtitle={t('dashboard.subtitle')} />
      }
      bottomNav={
        bottomNavItems.length > 0 ? (
          <BottomNav items={bottomNavItems} label={t('dashboard.communityLinks')} />
        ) : null
      }
    >
      <section className="mb-8">
        <Input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
          aria-label={t('search.placeholder')}
          placeholder={t('search.placeholder')}
          leftAddon={<Icon icon={Search} size={18} aria-hidden="true" />}
        />
      </section>

      <section className="mb-10" aria-live="polite">
        <SectionHeader title={t(query.trim() ? 'search.results' : 'trending.title')} />
        {discovery.loading ? <Loading label={t('common:states.loading')} /> : null}
        {discovery.error ? (
          <ErrorState message={t('search.failed')} onRetry={() => void discovery.reload()} />
        ) : null}
        {!discovery.loading && !discovery.error && !discovery.data?.length ? (
          <EmptyState
            title={t('search.empty')}
            primaryAction={
              query ? (
                <Button variant="outline" onClick={() => setQuery('')}>
                  {t('common:actions.clear')}
                </Button>
              ) : undefined
            }
          />
        ) : null}
        <div className="flex gap-4 overflow-x-auto pb-2">
          {discovery.data?.map((item) => (
            <Card
              key={`${item.service}:${item.id}`}
              className="w-[85%] shrink-0 md:w-80"
              interactive
            >
              <Button
                variant="ghost"
                className="h-auto w-full flex-col items-stretch p-0 text-left"
                onClick={() => navigate(item.route)}
              >
                <Image src={item.imageUrl ?? undefined} alt={item.title} ratio={16 / 9} />
                <CardContent>
                  <Badge variant="neutral">
                    {t(SERVICE_DESCRIPTORS[item.service]?.titleKey ?? '')}
                  </Badge>
                  <CardTitle className="line-clamp-2">{item.title}</CardTitle>
                </CardContent>
              </Button>
            </Card>
          ))}
        </div>
      </section>

      <section className="mb-12">
        <SectionHeader title={t('categories.title')} />
        <CardGrid columns={{ base: 2, md: 4 }}>
          {visibleCategories.map((category) => {
            const isEnabled = !category.serviceId || activeServices.includes(category.serviceId);
            const descriptor = category.serviceId ? SERVICE_DESCRIPTORS[category.serviceId] : null;
            const content = (
              <>
                <Badge
                  variant={isEnabled ? category.variant : 'neutral'}
                  icon={<Icon icon={category.icon} size={20} aria-hidden="true" />}
                  aria-hidden="true"
                />
                <CardTitle>{t(category.titleKey)}</CardTitle>
              </>
            );

            if (isEnabled && descriptor) {
              return (
                <Card key={category.id} asChild interactive>
                  <Button
                    variant="ghost"
                    className="h-full w-full flex-col items-start justify-between gap-4 p-5 text-left"
                    onClick={() => navigate(descriptor.route)}
                  >
                    {content}
                  </Button>
                </Card>
              );
            }

            return (
              <Card key={category.id} className="h-full p-5">
                <div className="flex h-full flex-col items-start justify-between gap-4">
                  {content}
                </div>
              </Card>
            );
          })}
        </CardGrid>
      </section>

      <section className="mb-8">
        <Card>
          <CardContent className="flex flex-col items-start gap-4">
            <Badge variant="primary" icon={<Icon icon={Rocket} size={18} aria-hidden="true" />}>
              {t('partnership.title')}
            </Badge>
            <CardDescription>{t('partnership.subtitle')}</CardDescription>
            <Button variant="primary">{t('partnership.action')}</Button>
          </CardContent>
        </Card>
      </section>

      {Object.keys(staffRoles).length > 0 ? (
        <section className="mb-12">
          <SectionHeader title={t('staff.title')} />
          <List>
            {Object.entries(staffRoles).map(([serviceId, roles]) => {
              const descriptor = STAFF_TOOL_DESCRIPTORS[serviceId];
              if (!descriptor || !descriptor.canAccess(roles)) {
                return null;
              }

              return (
                <ListItem
                  key={serviceId}
                  interactive
                  onClick={() => navigate(descriptor.route)}
                  leading={<Icon icon={descriptor.icon} size={20} aria-hidden="true" />}
                >
                  <div className="space-y-1">
                    <CardTitle>{t(descriptor.titleKey)}</CardTitle>
                    <CardDescription>{t(descriptor.descriptionKey)}</CardDescription>
                  </div>
                </ListItem>
              );
            })}
          </List>
        </section>
      ) : null}
    </ScreenScaffold>
  );
}
